import React, { useEffect, useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import {
  Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, InputAdornment,
  Radio, RadioGroup, Switch, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { FEE_QUERY_FIELDS } from '../components/PartnerSignups/FeeFields'
import { Icon, T } from '../components/PartnerSignups/onboardingDesign'
import {
  Badge, Card, CardTitle, FieldLabel, GhostButton, LinkButton, Notice, OnboardingPage, OutlineButton, PageHeader, PrimaryButton, StatCard,
} from '../components/PartnerSignups/onboardingUi'
import {
  ChangeFeesDialog, INVITES, InviteCard, InvitesTable, ReferralsTable, Rule, STANDARD, SignupLinkCard,
  headCell, money, pct, tabSx, tableSx,
} from '../components/Agents/PortalParts'
import {
  BANK_FIELDS, ISO_AGENT_FIELDS, ISO_RATE_FIELDS, MONTH_FIELDS, MonthAccounts, PAYOUT_FIELDS, PayoutsTable,
  PlatformStats, RatesTable, STATEMENT_FIELDS, StatementsList, bonusText,
} from '../components/Agents/isoParts'
import { timeAgo } from '../utils/authToken'

/**
 * An ISO's home (resolvers/iso.js): what it earns per platform, its sign-up
 * link, its agents (it adds them and sets each one's split and bonus), every
 * account it and its agents bring in, this month's earnings, past months and
 * payouts, and who RushMyFood pays. The ISO's login is an agent login, so the
 * link, invitations, applications and fees work as in the agent portal
 * (views/AgentPortal.jsx, which shows this page to an ISO).
 * Styled like the merchant sign-up form (onboardingUi).
 */

const MY_ISO = gql`
  query MyIso {
    myIso {
      _id corporateName email phone payoutTo extraShare code link
      bank { ${BANK_FIELDS} }
      rates { ${ISO_RATE_FIELDS} }
      stats { agents activeAgents started approved accounts pendingAmount paidAmount owedByIso }
    }
  }
`
const MY_AGENTS = gql`query MyIsoAgents { myIsoAgents { ${ISO_AGENT_FIELDS} } }`
const MY_PORTFOLIO = gql`
  query MyIsoPortfolio {
    myIsoPortfolio {
      _id agentId agentName restaurantName contactEmail businessType status startedAt signedAt approvedAt commissionAmount commissionStatus
      pricing held requestNote canChangeFees canOpen
      fees { ${FEE_QUERY_FIELDS} }
    }
  }
`
const MY_MONTH = gql`query MyIsoMonth { myIsoMonth { ${MONTH_FIELDS} } }`
const MY_STATEMENTS = gql`query MyIsoStatements { myIsoStatements { ${STATEMENT_FIELDS} } }`
const MY_PAYOUTS = gql`query MyIsoPayouts { myIsoPayouts { ${PAYOUT_FIELDS} } }`
const CREATE_AGENT = gql`mutation CreateIsoAgent($input: IsoAgentInput!) { createIsoAgent(input: $input) { _id code } }`
const UPDATE_AGENT = gql`mutation UpdateIsoAgent($id: ID!, $input: IsoAgentInput!) { updateIsoAgent(id: $id, input: $input) { _id split bonus isActive } }`
const SET_PAYOUT_TO = gql`mutation SetIsoPayoutTo($payoutTo: String!) { setIsoPayoutTo(payoutTo: $payoutTo) { _id payoutTo } }`
const SET_PAYOUT_STATUS = gql`mutation SetIsoPayoutStatusAsIso($id: ID!, $status: String!) { setIsoPayoutStatus(id: $id, status: $status) { _id status paidAt } }`

const APP_PAGE = '/agent/application'
const mutedLink = { color: T.muted, '&:hover': { color: T.text, background: T.fill } }
const sum = (rows, pick) => rows.reduce((s, x) => s + (Number(pick(x)) || 0), 0)

/** The rules, with this ISO's own rates. */
function IsoHowItWorks({ iso }) {
  return (
    <Card>
      <CardTitle sub="How you and your agents bring restaurants to RushMyFood, and how you are paid.">How it works</CardTitle>
      <Rule icon={Icon.Link} title="1. Bring accounts in">
        <li>Share your own sign-up link, or start an application yourself and send it to the restaurant to finish and sign.</li>
        <li>Add your agents on this page. Each gets a login and a link of their own. Every account they bring in is in your portfolio too.</li>
      </Rule>
      <Rule icon={Icon.Coins} title="2. What you earn">
        <li>On every order from each account in your portfolio, for as long as it stays with RushMyFood:</li>
        <Box component="li" sx={{ listStyle: 'none', ml: -2.25, mt: 1 }}>
          <RatesTable rates={iso.rates} />
        </Box>
        <li>The subtotal is the price of the items before discounts. Tax, tips, delivery and service fees aren’t included. Cancelled, unpaid and refunded orders don’t count.</li>
        <li>Each account keeps the rates you had on the day it was approved. DoorDash, Uber Eats and Grubhub orders count once RushMyFood manages them.</li>
        <li>Set an account’s fees above the minimums and you also earn <strong>{pct(iso.extraShare)}</strong> of the difference. For example, delivery at 30% against the 20% minimum earns you {pct(iso.extraShare)} of the extra 10% on its delivery orders.</li>
      </Rule>
      <Rule icon={Icon.Users} title="3. Your agents">
        <li>You set each agent’s split, a % of what you earn on the accounts they bring in.</li>
        <li>You can also give them a bonus per account once it reaches a number of orders you choose, for example $100 after its first 50 orders. Orders on every platform count.</li>
        <li>Both come out of your earnings. Each account keeps the split and bonus the agent had when it was approved.</li>
        <li>Your agents see only their own accounts and what they earn. They don’t see your rates.</li>
      </Rule>
      <Rule icon={Icon.Calendar} title="4. When you are paid">
        <li>Earnings are added up by month (New York time) after each month ends.</li>
        <li>You choose who RushMyFood pays: you, with you paying your agents, or each agent their share directly and you the rest.</li>
      </Rule>
      <Rule icon={Icon.Pen} title="5. Fees and signing">
        <li>The order percentages, the third-party orders % and the service fee are minimums. Set them higher and they apply right away. Going below a minimum, or changing any other fee, needs a super admin’s approval, and the restaurant can’t sign until it’s approved. You can always send it at the standard fees instead.</li>
        <li>Only the restaurant’s owner can accept the terms and sign the agreement.</li>
      </Rule>
    </Card>
  )
}

/** Who RushMyFood pays, the ISO's choice, and where its money goes. */
function PayoutCard({ iso, onSaved }) {
  const [choice, setChoice] = useState(iso.payoutTo)
  const [save, { loading }] = useMutation(SET_PAYOUT_TO)
  const [notice, setNotice] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => setChoice(iso.payoutTo), [iso.payoutTo])

  const submit = async () => {
    setNotice(null)
    setError(null)
    try {
      await save({ variables: { payoutTo: choice } })
      setNotice('Saved. It applies from this month on.')
      onSaved()
    } catch (e) {
      setError(e.message)
    }
  }

  return (
    <Card>
      <CardTitle sub={iso.bank && iso.bank.isSet ? `We deposit to ${iso.bank.bankName || 'your bank'} ${iso.bank.account}.` : 'RushMyFood hasn’t added your bank details yet. Contact us to add them.'}>
        How you’re paid
      </CardTitle>
      {notice && <Notice severity="success" onClose={() => setNotice(null)}>{notice}</Notice>}
      {error && <Notice severity="error" onClose={() => setError(null)}>{error}</Notice>}
      <RadioGroup value={choice} onChange={e => setChoice(e.target.value)}>
        <FormControlLabel value="ISO" control={<Radio />} label="Pay us everything. We pay our agents." sx={{ mb: 0.5 }} />
        <FormControlLabel value="AGENTS" control={<Radio />} label="Pay each agent their share directly, and us the rest." />
      </RadioGroup>
      <Typography sx={{ color: T.hint, fontSize: 13, mt: 1 }}>
        Paying agents directly: their shares and bonuses come out of your month. If they come to more than you earned, the difference comes out of the next month.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
        <PrimaryButton onClick={submit} busy={loading} disabled={choice === iso.payoutTo}>Save</PrimaryButton>
      </Box>
    </Card>
  )
}

/** Add an agent, or change one's split, bonus or details. */
function IsoAgentDialog({ agent, onClose, onSaved }) {
  const editing = !!agent
  const [f, setF] = useState({
    name: agent ? agent.name : '',
    email: agent ? agent.email : '',
    phone: agent ? agent.phone || '' : '',
    split: agent ? String(agent.split ?? '') : '50',
    bonus: agent && Number(agent.bonus) ? String(agent.bonus) : '',
    bonusOrders: agent && agent.bonusOrders ? String(agent.bonusOrders) : '50',
    isActive: agent ? agent.isActive : true,
  })
  const [error, setError] = useState(null)
  const [create, { loading: creating }] = useMutation(CREATE_AGENT)
  const [update, { loading: updating }] = useMutation(UPDATE_AGENT)
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }))
  const busy = creating || updating

  const save = async () => {
    setError(null)
    const input = {
      name: f.name,
      phone: f.phone || null,
      split: f.split === '' ? null : Number(f.split),
      bonus: f.bonus === '' ? 0 : Number(f.bonus),
      bonusOrders: f.bonusOrders === '' ? null : Number(f.bonusOrders),
    }
    try {
      if (editing) await update({ variables: { id: agent._id, input: { ...input, isActive: f.isActive } } })
      else await create({ variables: { input: { ...input, email: f.email } } })
      onSaved()
    } catch (e) {
      setError(e.message)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `Edit ${agent.name}` : 'Add an agent'}</DialogTitle>
      <DialogContent dividers sx={{ borderColor: T.divider }}>
        {error && <Notice severity="error">{error}</Notice>}
        {!editing && (
          <Typography variant="body2" sx={{ color: T.text2, mb: 2.5 }}>
            Your agent gets an email with a temporary password and their own sign-up link. They see only the accounts they bring in and what they earn.
          </Typography>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FieldLabel htmlFor="iso-agent-name" label="Name" />
            <TextField id="iso-agent-name" fullWidth size="small" required value={f.name} onChange={set('name')} />
          </Grid>
          <Grid item xs={12} sm={7}>
            <FieldLabel htmlFor="iso-agent-email" label="Email" />
            <TextField id="iso-agent-email" fullWidth size="small" type="email" required disabled={editing} value={f.email} onChange={set('email')} />
          </Grid>
          <Grid item xs={12} sm={5}>
            <FieldLabel htmlFor="iso-agent-phone" label="Phone" optional />
            <TextField id="iso-agent-phone" fullWidth size="small" type="tel" value={f.phone} onChange={set('phone')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel htmlFor="iso-agent-split" label="Their split" />
            <TextField
              id="iso-agent-split" fullWidth size="small" type="number" value={f.split} onChange={set('split')}
              inputProps={{ min: 0, max: 100, step: 1 }}
              InputProps={{ endAdornment: <InputAdornment position="end">% of what you earn</InputAdornment> }}
              helperText="On the accounts they bring in."
            />
          </Grid>
          <Grid item xs={12} sm={6} />
          <Grid item xs={12} sm={6}>
            <FieldLabel htmlFor="iso-agent-bonus" label="Bonus per account" optional />
            <TextField
              id="iso-agent-bonus" fullWidth size="small" type="number" value={f.bonus} onChange={set('bonus')} placeholder="0"
              inputProps={{ min: 0, step: 5 }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel htmlFor="iso-agent-bonus-orders" label="Paid once the account reaches" />
            <TextField
              id="iso-agent-bonus-orders" fullWidth size="small" type="number" value={f.bonusOrders} onChange={set('bonusOrders')} disabled={!Number(f.bonus)}
              inputProps={{ min: 1, step: 10 }}
              InputProps={{ endAdornment: <InputAdornment position="end">orders</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ color: T.hint, fontSize: 13 }}>
              {Number(f.bonus)
                ? `${bonusText(f.bonus, f.bonusOrders || 50)}: once for each account they bring in, counting its orders on every platform from the day it’s approved.`
                : 'No bonus. Add an amount to pay one once an account reaches a number of orders.'}
              {' '}The split and bonus come out of your earnings and apply to accounts approved from now on.
            </Typography>
          </Grid>
          {editing && (
            <Grid item xs={12}>
              <FormControlLabel
                sx={{ '& .MuiFormControlLabel-label': { fontWeight: 600, fontSize: 14 } }}
                control={<Switch checked={f.isActive} onChange={e => setF(x => ({ ...x, isActive: e.target.checked }))} />}
                label={f.isActive ? 'Active' : 'Deactivated: they can’t sign in and their link stops working'}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <GhostButton onClick={onClose} disabled={busy}>Cancel</GhostButton>
        <PrimaryButton onClick={save} busy={busy} disabled={!f.name || (!editing && !f.email)}>
          {editing ? 'Save' : 'Add agent'}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  )
}

export default function IsoPortal({ me }) {
  const history = useHistory()
  const { data: d, loading, error, refetch: refetchIso } = useQuery(MY_ISO, { fetchPolicy: 'network-only' })
  const { data: a, refetch: refetchAgents } = useQuery(MY_AGENTS, { fetchPolicy: 'network-only' })
  const { data: pf, refetch: refetchPortfolio } = useQuery(MY_PORTFOLIO, { fetchPolicy: 'network-only' })
  const { data: m } = useQuery(MY_MONTH, { fetchPolicy: 'network-only' })
  const { data: st } = useQuery(MY_STATEMENTS, { fetchPolicy: 'network-only' })
  const { data: po, refetch: refetchPayouts } = useQuery(MY_PAYOUTS, { fetchPolicy: 'network-only' })
  const { data: i, refetch: refetchInvites } = useQuery(INVITES, { fetchPolicy: 'network-only' })
  const { data: std } = useQuery(STANDARD, { fetchPolicy: 'network-only' })
  const [setPayoutStatus, { loading: settingPayout }] = useMutation(SET_PAYOUT_STATUS)
  const [tab, setTab] = useState(0)
  const [agentDialog, setAgentDialog] = useState(null)
  const [changing, setChanging] = useState(null)
  const [notice, setNotice] = useState(null)
  const [actionError, setActionError] = useState(null)
  const openApplication = (id) => history.push(`${APP_PAGE}/${id || 'new'}`)

  if (loading) return <OnboardingPage><Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: T.red }} /></Box></OnboardingPage>
  if (error) return <OnboardingPage><Notice severity="error">{error.message}</Notice></OnboardingPage>
  const iso = d && d.myIso
  if (!iso) return null
  const agents = (a && a.myIsoAgents) || []
  const portfolio = (pf && pf.myIsoPortfolio) || []
  const month = m && m.myIsoMonth
  const statements = (st && st.myIsoStatements) || []
  const payouts = (po && po.myIsoPayouts) || []
  const invites = (i && i.myAgentInvites) || []
  const standard = std && std.merchantAgreementFees
  const shareOf = Object.fromEntries(((month && month.agents) || []).map(x => [x.agentId, x.share]))
  const toIso = payouts.filter(p => p.kind === 'ISO_MONTH')
  const owedToYou = sum(toIso.filter(p => p.status === 'PENDING'), p => p.amount)
  const paidToYou = sum(toIso.filter(p => p.status === 'PAID'), p => p.amount)

  const changePayout = async (id, status) => {
    setActionError(null)
    try {
      await setPayoutStatus({ variables: { id, status } })
      await refetchPayouts()
    } catch (e) {
      setActionError(e.message)
    }
  }

  return (
    <OnboardingPage>
      <PageHeader
        eyebrow="ISO portal"
        title={iso.corporateName || me.name}
        subtitle="Your agents, every account you bring to RushMyFood, and what you earn on them."
        actions={(
          <>
            <OutlineButton onClick={() => setAgentDialog({})}>
              <Icon.Users size={18} />
              Add an agent
            </OutlineButton>
            <PrimaryButton onClick={() => openApplication(null)}>
              <Icon.Plus size={18} />
              Start an application
            </PrimaryButton>
          </>
        )}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' }, gap: 2, mb: 3 }}>
        <StatCard label="Active agents" value={iso.stats.activeAgents} tone="purple" />
        <StatCard label="Accounts earning" value={iso.stats.accounts} tone="green" />
        <StatCard label="Earned this month" value={money(month && month.gross)} />
        <StatCard label="Agents’ share this month" value={money(month && month.agentShares)} />
        <StatCard label="Owed to you" value={money(owedToYou)} tone="red" />
        <StatCard label="Paid to you" value={money(paidToYou)} tone="green" />
      </Box>

      {notice && <Notice severity="success" onClose={() => setNotice(null)}>{notice}</Notice>}
      {actionError && <Notice severity="error" onClose={() => setActionError(null)}>{actionError}</Notice>}

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <IsoHowItWorks iso={iso} />
        </Grid>
        <Grid item xs={12} md={5}>
          <Box sx={{ display: 'grid', gap: 2.5 }}>
            <SignupLinkCard link={iso.link} code={iso.code} />
            <PayoutCard iso={iso} onSaved={() => { refetchIso(); refetchPayouts() }} />
            <InviteCard onSent={() => { refetchInvites(); refetchPortfolio() }} />
          </Box>
        </Grid>
      </Grid>

      {month && month.platforms && month.platforms.length > 0 && (
        <Card sx={{ mb: 3, p: 0, overflow: 'hidden' }}>
          <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}>
            <CardTitle sub="Orders on every account in your portfolio, by platform. Each account’s numbers are under Earnings.">
              {month.label} orders
            </CardTitle>
          </Box>
          <Box sx={{ overflowX: 'auto' }}>
            <PlatformStats month={month} />
          </Box>
        </Card>
      )}

      <Card sx={{ p: 0, overflow: 'hidden' }}>
        <Tabs
          value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile
          sx={{ borderBottom: `1px solid ${T.divider}`, px: { xs: 1, sm: 2 }, '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' } }}
        >
          <Tab label={`Accounts (${portfolio.length})`} sx={tabSx} />
          <Tab label={`Agents (${agents.length})`} sx={tabSx} />
          <Tab label="Earnings" sx={tabSx} />
          <Tab label={`Payouts (${payouts.length})`} sx={tabSx} />
          <Tab label={`Invitations sent (${invites.length})`} sx={tabSx} />
        </Tabs>
        <Box sx={{ overflowX: 'auto' }}>
          {tab === 0 ? (
            <ReferralsTable
              referrals={portfolio} standard={standard} onOpen={openApplication} onChangeFees={setChanging}
              agentColumn commissionHeader="Agent bonus" emptyText="No sign-ups from you or your agents yet."
            />
          ) : tab === 1 ? (
            agents.length === 0 ? (
              <Box sx={{ p: 5, textAlign: 'center' }}>
                <Typography sx={{ color: T.hint, mb: 1.5 }}>You haven’t added any agents yet.</Typography>
                <OutlineButton onClick={() => setAgentDialog({})}><Icon.Plus size={16} />Add an agent</OutlineButton>
              </Box>
            ) : (
              <Table size="small" sx={{ minWidth: 960, ...tableSx }}>
                <TableHead>
                  <TableRow>
                    {['Agent', 'Link code', 'Split', 'Bonus', 'Restaurants', 'Started', 'Approved', 'This month', 'Owed', 'Paid', ''].map(h => (
                      <TableCell key={h} sx={headCell}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agents.map(x => (
                    <TableRow key={x._id} sx={{ opacity: x.isActive ? 1 : 0.55 }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: T.text }}>{x.name}</Typography>
                        <Typography variant="caption" sx={{ color: T.muted, fontSize: 13 }}>{x.email}</Typography>
                        {!x.isEmailVerified && <Badge kind="amber" sx={{ ml: 1, fontSize: 11.5, px: 1 }}>Email not verified</Badge>}
                        {!x.isActive && <Badge kind="red" sx={{ ml: 1, fontSize: 11.5, px: 1 }}>Deactivated</Badge>}
                        {iso.payoutTo === 'AGENTS' && !x.bankSet && <Badge kind="amber" sx={{ ml: 1, fontSize: 11.5, px: 1 }}>No bank details</Badge>}
                        <Typography sx={{ color: x.lastLoginAt ? T.text2 : T.hint, fontSize: 12.5, mt: 0.25 }}>
                          {x.lastLoginAt ? `Last login ${timeAgo(x.lastLoginAt)}` : 'No sign-in recorded yet'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13.5, color: T.text2, whiteSpace: 'nowrap' }}>{x.code}</TableCell>
                      <TableCell>{pct(x.split)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{bonusText(x.bonus, x.bonusOrders)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {x.stats ? x.stats.restaurants : 0}
                        <Typography sx={{ color: T.hint, fontSize: 12.5 }}>{x.stats ? x.stats.signedUp : 0} signed up · {x.stats ? x.stats.assigned : 0} assigned</Typography>
                      </TableCell>
                      <TableCell>{x.stats ? x.stats.started : 0}</TableCell>
                      <TableCell>{x.stats ? x.stats.approved : 0}</TableCell>
                      <TableCell>{money(shareOf[x._id])}</TableCell>
                      <TableCell sx={{ color: T.amberText, fontWeight: 600 }}>{money(x.stats && x.stats.pendingAmount)}</TableCell>
                      <TableCell>{money(x.stats && x.stats.paidAmount)}</TableCell>
                      <TableCell align="right">
                        <LinkButton size="small" onClick={() => setAgentDialog({ agent: x })}>Edit</LinkButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : tab === 2 ? (
            <Box>
              {month && (
                <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 1 }}>
                  <CardTitle sub={`So far: ${money(month.gross)} earned on ${month.orders} order${month.orders === 1 ? '' : 's'}. Your agents’ share is ${money(month.agentShares)}${month.agentsPaidBy === 'RUSHMYFOOD' ? ', paid by RushMyFood' : ', paid by you'}.${month.carryIn < 0 ? ` ${money(Math.abs(month.carryIn))} carried over from last month.` : ''} Owed to you for the month so far: ${money(month.isoAmount)}.`}>
                    {month.label}
                  </CardTitle>
                </Box>
              )}
              <MonthAccounts accounts={month ? month.accounts : []} />
              {statements.length > 0 && (
                <Box sx={{ borderTop: `1px solid ${T.divider}` }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, px: { xs: 2, sm: 3 }, pt: 2, pb: 1 }}>Past months</Typography>
                  <StatementsList statements={statements} forIso />
                </Box>
              )}
            </Box>
          ) : tab === 3 ? (
            <Box>
              <Typography sx={{ color: T.hint, fontSize: 13.5, px: { xs: 2, sm: 3 }, pt: 2 }}>
                What RushMyFood pays you and your agents, and what you owe your agents yourself. Mark those paid when you pay them.
              </Typography>
              <PayoutsTable
                payouts={payouts}
                emptyText="Nothing yet. Each month is added here after it ends."
                actions={p => (p.payer !== 'ISO' ? null : p.status === 'PENDING' ? (
                  <LinkButton size="small" disabled={settingPayout} onClick={() => changePayout(p._id, 'PAID')}>Mark paid</LinkButton>
                ) : (
                  <LinkButton size="small" disabled={settingPayout} onClick={() => changePayout(p._id, 'PENDING')} sx={mutedLink}>Undo</LinkButton>
                ))}
              />
            </Box>
          ) : (
            <InvitesTable invites={invites} />
          )}
        </Box>
      </Card>

      {agentDialog && (
        <IsoAgentDialog
          agent={agentDialog.agent}
          onClose={() => setAgentDialog(null)}
          onSaved={() => {
            setNotice(agentDialog.agent ? 'Saved.' : 'Agent added. They’ve been emailed their login.')
            setAgentDialog(null)
            refetchAgents()
            refetchIso()
          }}
        />
      )}
      {changing && standard && (
        <ChangeFeesDialog
          referral={changing}
          standard={standard}
          extraText={`You earn ${pct(iso.extraShare)} of whatever is above the minimums, on top of your rates. On your agents’ accounts they get their split of it.`}
          onClose={() => setChanging(null)}
          onDone={(message) => { setChanging(null); setNotice(message); refetchPortfolio() }}
        />
      )}
    </OnboardingPage>
  )
}
